import React, { useCallback, useEffect, useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import * as api from '../../api/projectsApi';
import type { ApiProjectRow } from '../../types/project';
import styles from '../Management.module.css';

type Props = { onOpen: (view: 'visual-editor' | 'preview') => void; onPublish: (id: number) => void };
export default function Projects({ onOpen, onPublish }: Props): React.ReactElement {
  const current = useProject();
  const [projects, setProjects] = useState<ApiProjectRow[]>([]);
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false);
  const [error, setError] = useState(''), [notice, setNotice] = useState(''), [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false), [name, setName] = useState(''), [description, setDescription] = useState('');
  const [editing, setEditing] = useState<number | null>(null), [editName, setEditName] = useState(''), [editDescription, setEditDescription] = useState('');
  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try { setProjects(await api.listProjects()); }
    catch (e) { setError(e instanceof Error ? e.message : 'Projects could not be loaded.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  const pending = busy || current.isLoading || current.isSaving;
  const mayReplace = () => !current.isDirty || window.confirm('Discard the unsaved changes to "' + current.projectName + '" and continue?');
  async function perform(action: () => Promise<void>) {
    setBusy(true); setError(''); setNotice('');
    try { await action(); }
    catch (e) { setError(e instanceof Error ? e.message : 'The project operation failed.'); }
    finally { setBusy(false); }
  }
  async function open(project: ApiProjectRow, view: 'visual-editor' | 'preview') {
    if (project.id === current.projectRowId && current.isDirty) { onOpen(view); return; }
    if (!mayReplace()) return;
    await perform(async () => {
      if (!await current.loadProject(project.id)) throw new Error('The project could not be opened. Refresh the list and try again.');
      onOpen(view);
    });
  }
  async function create(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim() || !mayReplace()) return;
    await perform(async () => {
      const saved = await api.createSavedProject(name.trim(), description.trim());
      setCreating(false); setName(''); setDescription('');
      await refresh();
      if (await current.loadProject(saved.id)) onOpen('visual-editor');
      else setNotice('The project was created. Open it from this list when the connection is available.');
    });
  }
  async function rename(event: React.FormEvent, project: ApiProjectRow) {
    event.preventDefault();
    await perform(async () => {
      if (project.id === current.projectRowId && current.isDirty) throw new Error('Save your current editor changes before renaming this project from the list.');
      await api.updateProjectDetails(project.id, editName.trim(), editDescription.trim(), project.updated_at);
      setEditing(null);
      if (project.id === current.projectRowId) await current.loadProject(project.id);
      await refresh(); await current.refreshProjectList();
      setNotice('Project details updated.');
    });
  }
  async function remove(project: ApiProjectRow) {
    const extra = project.id === current.projectRowId && current.isDirty ? ' Its unsaved editor changes will also be discarded.' : '';
    if (!window.confirm('Delete "' + project.name + '"? This cannot be undone.' + extra)) return;
    await perform(async () => {
      await api.deleteSavedProject(project.id, project.updated_at);
      if (project.id === current.projectRowId) current.newProject();
      if (editing === project.id) setEditing(null);
      await refresh(); await current.refreshProjectList(); setNotice('Project deleted.');
    });
  }
  const shown = projects.filter(project => (project.name + ' ' + project.description).toLocaleLowerCase().includes(query.toLocaleLowerCase()));
  return <section className={styles.page}>
    <div className={styles.header}><div><h1>Projects</h1><p className={styles.muted}>Build, save, and edit your Conductor projects. Publish a saved project when it is ready to use.</p></div>
      <div className={styles.actions}><button className={styles.button} disabled={loading || pending} onClick={() => void refresh()}>Refresh projects</button>
      <button className={styles.primary} disabled={pending} onClick={() => setCreating(true)}>New project</button></div></div>
    {current.isDirty && <p className={styles.notice}>You have unsaved changes to <strong>{current.projectName}</strong>. <button className={styles.button} onClick={() => onOpen('visual-editor')}>Continue editing</button></p>}
    {error && <p role="alert" className={styles.error}>{error}</p>}
    {notice && <p role="status" className={styles.notice}>{notice}</p>}
    {creating && <form className={styles.form} onSubmit={event => void create(event)}>
      <label className={styles.field}>Project name<input autoFocus required maxLength={200} value={name} onChange={event => setName(event.target.value)} /></label>
      <label className={styles.field}>Description<input maxLength={2000} value={description} onChange={event => setDescription(event.target.value)} /></label>
      <button className={styles.primary} disabled={pending || !name.trim()}>Create project</button>
      <button type="button" className={styles.button} disabled={pending} onClick={() => setCreating(false)}>Cancel</button>
    </form>}
    {loading && <p role="status">Loading projects…</p>}
    {!loading && !error && projects.length === 0 && <div className={styles.empty}><strong>No saved projects yet.</strong><p>Create your first project, or save your work from the Visual Editor or JSON Editor.</p></div>}
    {projects.length > 0 && <label className={styles.field} style={{maxWidth:380,marginBottom:20}}>Search projects<input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Find by name or description" /></label>}
    {!loading && !error && projects.length > 0 && shown.length === 0 && <p className={styles.empty}>No projects match your search.</p>}
    {!error && <div className={styles.grid}>{shown.map(project => <article className={styles.card} key={project.id} aria-label={'Project ' + project.name}>
      <span className={styles.badge}>{project.id === current.projectRowId ? 'Open in editor' : 'Conductor project'}</span>
      {editing === project.id ? <form className={styles.inlineForm} onSubmit={event => void rename(event, project)}>
        <label className={styles.field}>Project name<input required maxLength={200} value={editName} onChange={event => setEditName(event.target.value)} /></label>
        <label className={styles.field}>Description<textarea maxLength={2000} value={editDescription} onChange={event => setEditDescription(event.target.value)} /></label>
        <div className={styles.actions}><button className={styles.primary} disabled={pending || !editName.trim()}>Save details</button><button type="button" className={styles.button} disabled={pending} onClick={() => setEditing(null)}>Cancel</button></div>
      </form> : <><h3>{project.name}</h3><p className={styles.muted}>{project.description || 'No description yet.'}</p>
        <div className={styles.details}><span>Updated {new Date(project.updated_at).toLocaleString()}</span></div>
        <div className={styles.actions}>
          <button className={styles.primary} disabled={pending} onClick={() => void open(project, 'visual-editor')}>Open editor</button>
          <button className={styles.button} disabled={pending} onClick={() => void open(project, 'preview')}>Preview</button>
          <button className={styles.button} disabled={pending} onClick={() => onPublish(project.id)}>Publish</button>
          <button className={styles.button} disabled={pending || (project.id === current.projectRowId && current.isDirty)} onClick={() => {setEditing(project.id); setEditName(project.name); setEditDescription(project.description);}}>Rename</button>
          <button className={styles.danger} disabled={pending} onClick={() => void remove(project)}>Delete</button>
        </div></>}
    </article>)}</div>}
  </section>;
}
